import React, { useEffect, useMemo, useState } from 'react';
import {
  fetchConversations,
  createConversation,
  updateConversation,
  deleteConversation,
  type TaskingBotConversation,
} from '../endpoints/taskingbot/chat-history.schema';
import styles from './ConversationSidebar.module.css';

interface Props {
  /** Currently-selected conversation. */
  activeId: string | null;
  /** Filter by project (passes through to backend). Use '__global__' for unscoped. */
  projectId?: string;
  /** User clicked a row in the list. */
  onSelect: (id: string) => void;
  /** User clicked "New Chat". Caller is responsible for switching the chat pane. */
  onNew: (id: string) => void;
  /** Optional caller hook — fires after a conversation is renamed or deleted. */
  onMutated?: () => void;
  /** Force a refresh — bump this number to re-fetch the list. */
  refreshKey?: number;
  /** Whether the sidebar is open (controlled). */
  open: boolean;
  /** Caller's toggle handler. */
  onToggle: () => void;
}

type Group = {
  label: string;
  rows: TaskingBotConversation[];
};

function groupByRecency(list: TaskingBotConversation[]): Group[] {
  const now = Date.now();
  const day = 86_400_000;
  const buckets: Record<string, TaskingBotConversation[]> = {
    Pinned: [],
    Today: [],
    Yesterday: [],
    'Last 7 days': [],
    'Last 30 days': [],
    Older: [],
  };
  for (const c of list) {
    if (c.isPinned) {
      buckets.Pinned.push(c);
      continue;
    }
    const t = new Date(c.updatedAt || c.createdAt).getTime();
    const age = now - t;
    if (age < day) buckets.Today.push(c);
    else if (age < day * 2) buckets.Yesterday.push(c);
    else if (age < day * 7) buckets['Last 7 days'].push(c);
    else if (age < day * 30) buckets['Last 30 days'].push(c);
    else buckets.Older.push(c);
  }
  return Object.entries(buckets)
    .filter(([, rows]) => rows.length > 0)
    .map(([label, rows]) => ({ label, rows }));
}

function formatRelative(iso: string): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '';
  const diff = Math.max(0, Date.now() - t);
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  if (diff < 86_400_000 * 7) return `${Math.floor(diff / 86_400_000)}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function ConversationSidebar(props: Props) {
  const { activeId, projectId, onSelect, onNew, onMutated, refreshKey, open, onToggle } = props;
  const [list, setList] = useState<TaskingBotConversation[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Load conversations
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchConversations({ limit: 200, ...(projectId ? { projectId } : {}) })
      .then(({ conversations }) => {
        if (cancelled) return;
        setList(conversations);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || 'Failed to load chats');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, refreshKey]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (c) =>
        (c.title || '').toLowerCase().includes(q) ||
        (c.lastMessage || '').toLowerCase().includes(q),
    );
  }, [list, search]);

  const groups = useMemo(() => groupByRecency(filtered), [filtered]);

  const handleNew = async () => {
    try {
      const { conversation } = await createConversation({
        projectId: projectId === '__global__' ? undefined : projectId,
      });
      setList((prev) => [conversation, ...prev]);
      onNew(conversation.id);
    } catch (err: any) {
      setError(err?.message || 'Failed to create chat');
    }
  };

  const handleRename = async (id: string) => {
    const title = renameValue.trim();
    if (!title) {
      setRenamingId(null);
      return;
    }
    try {
      await updateConversation({ conversationId: id, title });
      setList((prev) => prev.map((c) => (c.id === id ? { ...c, title } : c)));
      setRenamingId(null);
      onMutated?.();
    } catch (err: any) {
      setError(err?.message || 'Failed to rename');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this chat? This cannot be undone.')) return;
    try {
      await deleteConversation(id);
      setList((prev) => prev.filter((c) => c.id !== id));
      onMutated?.();
    } catch (err: any) {
      setError(err?.message || 'Failed to delete');
    }
  };

  const handlePin = async (id: string, isPinned: boolean) => {
    try {
      await updateConversation({ conversationId: id, isPinned: !isPinned });
      setList((prev) => prev.map((c) => (c.id === id ? { ...c, isPinned: !isPinned } : c)));
      onMutated?.();
    } catch (err: any) {
      setError(err?.message || 'Failed to pin');
    }
  };

  if (!open) {
    return (
      <div className={styles.collapsedRail}>
        <button
          type="button"
          className={styles.expandButton}
          onClick={onToggle}
          title="Show chat history"
          aria-label="Show chat history"
        >
          ☰
        </button>
        <button
          type="button"
          className={styles.newChatRail}
          onClick={handleNew}
          title="New chat"
          aria-label="New chat"
        >
          +
        </button>
      </div>
    );
  }

  return (
    <aside className={styles.sidebar} aria-label="Chat history">
      <div className={styles.header}>
        <button type="button" className={styles.newChatButton} onClick={handleNew}>
          <span className={styles.plus}>+</span>
          <span>New Chat</span>
        </button>
        <button
          type="button"
          className={styles.collapseButton}
          onClick={onToggle}
          title="Hide sidebar"
          aria-label="Hide sidebar"
        >
          «
        </button>
      </div>

      <div className={styles.searchRow}>
        <input
          type="search"
          className={styles.searchInput}
          placeholder="Search chats…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {error && <div className={styles.error}>{error}</div>}

      <div className={styles.scroller}>
        {loading && list.length === 0 ? (
          <div className={styles.empty}>Loading…</div>
        ) : groups.length === 0 ? (
          <div className={styles.empty}>{search ? 'No matches' : 'No chats yet'}</div>
        ) : (
          groups.map((g) => (
            <div className={styles.group} key={g.label}>
              <div className={styles.groupLabel}>{g.label}</div>
              {g.rows.map((c) => {
                const isActive = c.id === activeId;
                return (
                  <div
                    key={c.id}
                    className={`${styles.row} ${isActive ? styles.rowActive : ''}`}
                  >
                    {renamingId === c.id ? (
                      <input
                        autoFocus
                        className={styles.renameInput}
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onBlur={() => handleRename(c.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleRename(c.id);
                          if (e.key === 'Escape') setRenamingId(null);
                        }}
                      />
                    ) : (
                      <button
                        type="button"
                        className={styles.rowMain}
                        onClick={() => onSelect(c.id)}
                        title={c.lastMessage || c.title}
                      >
                        <div className={styles.rowTitle}>
                          {c.isPinned && <span className={styles.pinIcon}>📌</span>}
                          {c.title || 'Untitled'}
                        </div>
                        <div className={styles.rowMeta}>
                          <span className={styles.rowTime}>
                            {formatRelative(c.updatedAt || c.createdAt)}
                          </span>
                          {c.messageCount > 0 && (
                            <span className={styles.rowCount}>· {c.messageCount}</span>
                          )}
                        </div>
                      </button>
                    )}

                    {renamingId !== c.id && (
                      <div className={styles.rowActions}>
                        <button
                          type="button"
                          className={styles.actionButton}
                          onClick={() => handlePin(c.id, !!c.isPinned)}
                          title={c.isPinned ? 'Unpin' : 'Pin'}
                        >
                          {c.isPinned ? '📌' : '📍'}
                        </button>
                        <button
                          type="button"
                          className={styles.actionButton}
                          onClick={() => {
                            setRenameValue(c.title || '');
                            setRenamingId(c.id);
                          }}
                          title="Rename"
                        >
                          ✎
                        </button>
                        <button
                          type="button"
                          className={`${styles.actionButton} ${styles.deleteButton}`}
                          onClick={() => handleDelete(c.id)}
                          title="Delete"
                        >
                          ✕
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>
    </aside>
  );
}

export default ConversationSidebar;
